import { Controller } from "@hotwired/stimulus"
import { showToast } from "../utils/toast"

export default class extends Controller {
  static targets = ["modal", "backdrop", "closeButton", "shareButton"]
  static values = {
    url: String,
    copyFailedText: { type: String, default: "Failed to copy link. Please try again." }
  }

  connect() {
    this.close = this.close.bind(this)
    this.closeOnEscape = this.closeOnEscape.bind(this)
    this.share = this.share.bind(this)

    document.addEventListener("keydown", this.closeOnEscape)
    if (this.hasBackdropTarget) {
      this.backdropTarget.addEventListener("click", this.close)
    }
    if (this.hasCloseButtonTarget) {
      this.closeButtonTarget.addEventListener("click", this.close)
    }
    this.shareButtonTargets.forEach(button => {
      button.addEventListener("click", this.share)
    })

    if (this.hasModalTarget) {
      this.modalElement = this.modalTarget
      this.originalParent = this.modalTarget.parentNode
    }
  }

  disconnect() {
    document.removeEventListener("keydown", this.closeOnEscape)
    if (this.hasBackdropTarget) {
      this.backdropTarget.removeEventListener("click", this.close)
    }
    if (this.hasCloseButtonTarget) {
      this.closeButtonTarget.removeEventListener("click", this.close)
    }
    this.shareButtonTargets.forEach(button => {
      button.removeEventListener("click", this.share)
    })
    document.documentElement.classList.remove('overflow-hidden')
    document.body.classList.remove('overflow-hidden')
  }

  open(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    if (this.modalElement) {
      if (this.modalElement.parentNode !== document.body) {
        document.body.appendChild(this.modalElement)
      }
      this.modalElement.classList.remove("hidden")
      this.modalElement.classList.add("flex")
      document.body.style.overflow = "hidden"
      setTimeout(() => {
        this.modalElement.classList.add("opacity-100", "scale-100")
        this.modalElement.classList.remove("opacity-0", "scale-95")
      }, 10)
    }
  }

  close(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    if (this.modalElement) {
      this.modalElement.classList.remove("opacity-100", "scale-100")
      this.modalElement.classList.add("opacity-0", "scale-95")
      setTimeout(() => {
        this.modalElement.classList.add("hidden")
        this.modalElement.classList.remove("flex")
        document.body.style.overflow = ""
        if (this.originalParent && this.modalElement.parentNode === document.body) {
          this.originalParent.appendChild(this.modalElement)
        }
      }, 200)
    }
  }

  closeOnEscape(event) {
    if (event.key === "Escape" && this.modalElement && !this.modalElement.classList.contains("hidden")) {
      this.close()
    }
  }

  share(event) {
    event.preventDefault()
    event.stopPropagation()
    const button = event.currentTarget
    const shareUrl = button.dataset.shareUrl
    const message = button.dataset.message
    const url = this.urlValue

    if (shareUrl) {
      window.open(shareUrl, "_blank")
      this.close()
    } else if (message) {
      this.copyToClipboard(url, message)
    }
  }

  async copyToClipboard(text, message) {
    let copied = false
    try {
      await navigator.clipboard.writeText(text)
      copied = true
    } catch (err) {
      const textArea = document.createElement("textarea")
      textArea.value = text
      textArea.style.position = "fixed"
      textArea.style.opacity = "0"
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()
      try {
        copied = document.execCommand("copy")
      } catch (e) {
        console.error("Fallback copy failed:", e)
      }
      document.body.removeChild(textArea)
    }

    if (copied) {
      showToast(message)
      this.close()
    } else {
      showToast(this.copyFailedTextValue, "alert")
    }
  }
}
